import { Link } from '../../../components/Link';
import {
  Lead,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('sender-reputation');

/**
 * The asset here is the domain-vs-IP split and what it implies on a SHARED IP — the thing that
 * makes people migrate ESPs and see no change, because they moved the half they did not own.
 */
export default function SenderReputationPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Sender reputation is a mailbox provider’s running score of how much your mail is wanted. It is not one number: your domain and your sending IP are scored separately, from different inputs, and on a shared provider IP you control the domain score and almost none of the IP one.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Two scores, not one">
        <p>
          Almost every deliverability conversation collapses these into "reputation", which makes
          the advice that follows unusable. They have different inputs, different timescales, and
          crucially different owners.
        </p>
        <LearnTable
          head={['', 'Domain reputation', 'IP reputation']}
          rows={[
            ['Attached to', 'The domain in the From: address', 'The sending server’s address'],
            ['Who owns it on a shared ESP IP', <Lead key="a">You, entirely</Lead>, <Lead key="b">Your ESP and every other customer</Lead>],
            ['Moves', 'Slowly — weeks to build, days to lose', 'Faster, and can swing on a neighbour'],
            ['Survives changing ESP', <Lead key="c">Yes — it follows you</Lead>, 'No — you inherit a new one'],
            ['Fix time', 'Weeks of good sending', 'Days, or immediately by moving'],
          ]}
        />
        <p>
          The row that matters is the last-but-one. Domain reputation follows you; IP reputation does
          not. So migrating ESPs fixes an IP problem and does nothing at all for a domain problem —
          which is why the migration so often produces no improvement, and why diagnosing which half
          is broken has to come first.
        </p>
      </LearnSection>

      <LearnSection title="What actually feeds the score">
        <p>
          Providers do not publish weightings, and anyone quoting exact percentages is guessing. The
          ordering below is well-established from provider documentation and postmaster tooling; the
          numbers are not, so there are none.
        </p>
        <LearnTable
          head={['Signal', 'Direction', 'Weight']}
          rows={[
            ['Spam complaints ("report spam")', 'Down, hard', <Lead key="a">Heaviest single input</Lead>],
            ['Hitting spam traps', 'Down, hard', 'Severe — a pristine trap is close to conclusive'],
            ['Hard bounces', 'Down', 'Heavy above roughly 2%'],
            ['Recipients rescuing mail from spam', 'Up', 'Strong positive, hard to engineer'],
            ['Replies', 'Up', 'Strong positive'],
            ['Opens and clicks', 'Up', 'Moderate — and noisy since image proxying'],
            ['Deleting without reading', 'Down', 'Mild but cumulative'],
            ['Volume consistency', 'Up', 'Steady beats spiky, always'],
            ['Authentication passing', 'Gate', <Lead key="b">Not a boost. Failing costs you</Lead>],
          ]}
        />
        <p>
          Gmail states a complaint-rate ceiling of 0.3%. That is roughly three complaints per
          thousand delivered messages — a much smaller budget than most senders assume, and the
          fastest way to lose a domain score you spent months building.
        </p>
        <p>
          Note the last row. Passing{' '}
          <Link to="/learn/email-authentication" className="text-accent underline underline-offset-4">
            SPF, DKIM and DMARC
          </Link>{' '}
          earns you nothing — it is the entry condition. Failing them is what costs.
        </p>
      </LearnSection>

      <LearnSection title="Diagnosing which half is broken">
        <LearnSubsection title="Symptoms of a domain problem">
          <p>
            Placement is bad across every provider and does not change when you send through a
            different service. Reputation in Google Postmaster Tools reads low or bad for the domain
            while the IP reads fine. Mail from a brand-new domain on the same infrastructure
            delivers normally.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Symptoms of an IP problem">
          <p>
            Placement is bad from one sending service and fine from another with the same From:
            domain and the same content. Or it degraded suddenly with no change on your side, which
            is the signature of a neighbour on a shared IP —{' '}
            <Link to="/learn/email-blacklist" className="text-accent underline underline-offset-4">
              worth a blacklist check
            </Link>{' '}
            at that point.
          </p>
        </LearnSubsection>
        <LearnSubsection title="The test that separates them">
          <p>
            Send identical content from the same domain through two different services. Same result
            from both means the domain. Different results mean the IP. It takes an afternoon and it
            saves you from migrating for no reason.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="Shared versus dedicated IP">
        <p>
          The choice is almost entirely a function of volume, and the usual instinct is backwards.
        </p>
        <LearnTable
          head={['', 'Shared IP', 'Dedicated IP']}
          rows={[
            ['Right for', 'Under roughly 100k messages a month', 'Sustained high volume'],
            ['Reputation', 'Pooled — you inherit the pool’s', 'Entirely yours, good and bad'],
            ['Starting from cold', <Lead key="a">Not an issue — the pool has history</Lead>, <Lead key="b">Zero history. Must be warmed</Lead>],
            ['Bad neighbour risk', 'Real, and outside your control', 'None'],
            ['Volume gaps', 'Harmless', 'Damaging — an idle IP goes cold'],
          ]}
        />
        <p>
          A dedicated IP at low volume is worse than a shared one. Providers need consistent volume
          to form an opinion, and an IP sending a few thousand messages a month never accumulates
          enough signal — so it is permanently treated as an unknown. The pooled reputation of a
          reputable ESP beats an unknown IP most of the time.
        </p>
      </LearnSection>

      <LearnSection title="Building it, and rebuilding it">
        <p>
          There is no fast path. Reputation is a moving average over weeks, so the only lever is
          consistent behaviour over time.
        </p>
        <LearnList ordered>
          <li>
            <Lead>Stop the bleeding first.</Lead> Suppress anyone who has not engaged in six months.
            Counter-intuitive, and it works: complaint rate is a ratio, and the fastest way to
            improve it is to stop mailing the people generating complaints.
          </li>
          <li>
            <Lead>Send to your most engaged recipients only</Lead> for two to four weeks. You are
            re-teaching the filter what your mail looks like when it is wanted.
          </li>
          <li>
            <Lead>Keep volume steady.</Lead> A predictable daily pattern is read as legitimate; a
            spike after silence is read as a compromised account.
          </li>
          <li>
            <Lead>Widen slowly</Lead>, in engagement order, watching complaints at each step.
          </li>
          <li>
            <Lead>Never buy a list</Lead> while recovering, or at all. One send to purchased data
            undoes months of this.
          </li>
        </LearnList>
        <p>
          On a genuinely new domain the same sequence is called{' '}
          <Link to="/learn/email-warmup" className="text-accent underline underline-offset-4">
            warmup
          </Link>
          , and the difference is only that you are building from nothing instead of digging out.
        </p>
      </LearnSection>

      <LearnSection title="Where to actually see it">
        <p>
          Google Postmaster Tools is the only first-party view worth having, and it is free: domain
          and IP reputation, complaint rate, authentication results and delivery errors, for Gmail.
          It requires verifying the domain by DNS and needs a few hundred messages a day before it
          shows anything.
        </p>
        <p>
          Microsoft offers SNDS and JMRP, both IP-based, both useful if you run your own
          infrastructure. Everything else is a third-party estimate — sometimes indicative, never
          the score itself. The providers do not publish it, and a vendor claiming to report{' '}
          <Tok>your reputation score</Tok> as a number is reporting their own model.
        </p>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What is the difference between domain and IP reputation?',
    a: 'Domain reputation attaches to the domain in your From: address and follows you everywhere, including to a new provider. IP reputation attaches to the sending server and is shared with every other customer on that address. On a shared ESP IP you control the first and almost none of the second.',
  },
  {
    q: 'How long does it take to build sender reputation?',
    a: 'Weeks of consistent sending to engaged recipients. It is a moving average, so there is no way to accelerate it — and it drops far faster than it climbs. A single send to a bad list can undo a month.',
  },
  {
    q: 'Should I get a dedicated IP?',
    a: 'Only at sustained high volume, roughly 100,000 messages a month upward. Below that a dedicated IP never accumulates enough signal for providers to form an opinion, so it stays an unknown quantity — which is worse than inheriting a reputable ESP’s pooled reputation.',
  },
  {
    q: 'What is an acceptable complaint rate?',
    a: 'Gmail states 0.3% as the ceiling, which is about three complaints per thousand delivered messages. That is a smaller budget than most senders expect, and exceeding it is the fastest way to lose a domain score built over months.',
  },
  {
    q: 'Where can I actually see my reputation?',
    a: 'Google Postmaster Tools for Gmail, and Microsoft SNDS for Outlook if you run your own infrastructure. Both are free and first-party. Everything else is a third-party estimate — useful as a signal, but not the score the provider is actually using.',
  },
  {
    q: 'I switched ESPs and nothing improved. Why?',
    a: 'Because the problem was your domain, not your IP. Changing provider gives you a new IP reputation and carries your domain reputation across unchanged. Run the same content from two services before migrating — identical results point at the domain.',
  },
];
